import {
  Box,
  Grid,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Autocomplete,
  Typography,
  CircularProgress,
  MenuItem,
} from "@mui/material";
import { useState, useEffect, useMemo } from "react";
import { AddCircleOutlineOutlined } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/Index";
import { ClientInformationProps } from "../../../types/steps";
import { BookingFieldsConfig } from "../../../types/bookings";
import CustomTextField from "../../../components/common/Textfield";
import apiService from "../../../service/Api";
import { toast } from "react-toastify";
import AddBookingFormFields from "../components/AddBookingFormFields";
import { MuiTelInput, MuiTelInputInfo } from "mui-tel-input";
import { IUser } from "../../../types/team-assignment";
import { IContactFormData } from "../../../types/contact";
import { useNavigate } from "react-router-dom";
import { ArrowRightCircle } from "lucide-react";

interface IBranch {
  id?: string | number;
  company_id: string | number;
  name: string;
  branch_rep: string;
  email: string;
  phone_number: string;
}

const BookingClientInformation: React.FC<ClientInformationProps> = ({
  serviceType,
  bookingData,
  setBookingData,
  currentContact,
}) => {
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const [companyOptions, setCompanyOptions] = useState<IContactFormData[]>([]);
  const [newCompanyData, setNewCompanyData] = useState<
    Partial<IContactFormData>
  >({
    name: "",
    email: "",
    phone: "",
  });
  const [savingNewCompany, setSavingNewCompany] = useState(false);
  const [branchOptions, setBranchOptions] = useState<IBranch[]>([]);
  const [contactOptions, setContactOptions] = useState<IContactFormData[]>([]);
  const [newBranch, setNewBranch] = useState<IBranch>({
    company_id: "",
    name: "",
    branch_rep: "",
    email: "",
    phone_number: "",
  });
  const [openAddBranchDialog, setOpenAddBranchDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [branchLoading, setBranchLoading] = useState(false);
  const [contactLoading, setContactLoading] = useState(false);
  const [companyLoading, setCompanyLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [openAddContactDialog, setOpenAddContactDialog] = useState(false);
  const [openAddCompanyDialog, setOpenAddCompanyDialog] = useState(false);
  const [newContactData, setNewContactData] = useState<
    Partial<IContactFormData>
  >({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
  });
  const [savingNewContact, setSavingNewContact] = useState(false);
  const [contactSearchTerm, setContactSearchTerm] = useState("");
  const [companySearchTerm, setCompanySearchTerm] = useState("");
  const navigate = useNavigate();
  // Fetch branches when company_id changes
  useEffect(() => {
    const fetchBranches = async () => {
      if (!bookingData.company_id) {
        setBranchOptions([]);
        return;
      }
      setBranchLoading(true);
      try {
        const branchResponse = await apiService.get<{ results: IBranch[] }>(
          `/api/companies/company-branch/?company_id=${bookingData.company_id}`
        );
        setBranchOptions(branchResponse.data.results || []);
      } catch (err: any) {
        console.error("Failed to fetch branches:", err);
        toast.error("Failed to load branches. Please try again.", {
          position: "top-right",
          autoClose: 5000,
          theme: "light",
        });
      } finally {
        setBranchLoading(false);
      }
    };

    fetchBranches();
  }, [bookingData.company_id]);

  // Debounced fetch for contacts
  useEffect(() => {
    const timer = setTimeout(() => {
      if (contactSearchTerm.length >= 3) {
        fetchContacts(contactSearchTerm);
      } else {
        setContactOptions([]);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [contactSearchTerm]);

  // Debounced fetch for companies
  useEffect(() => {
    const timer = setTimeout(() => {
      if (companySearchTerm.length >= 3) {
        fetchCompanies(companySearchTerm);
      } else {
        setCompanyOptions([]);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [companySearchTerm]);

  const fetchContacts = async (search: string) => {
    setContactLoading(true);
    try {
      const contactResponse = await apiService.get<{
        results: IContactFormData[];
      }>(`/api/contacts/contact/?search=${search}`);
      setContactOptions(contactResponse.data.results || []);
    } catch (err: any) {
      console.error("Failed to fetch contacts:", err);
      toast.error("Failed to load contacts. Please try again.", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
    } finally {
      setContactLoading(false);
    }
  };

  const fetchCompanies = async (search: string) => {
    setCompanyLoading(true);
    try {
      const companyResponse = await apiService.get<{
        results: IContactFormData[];
      }>(`/api/contacts/contact/?search=${search}&contact_type=company`);
      setCompanyOptions(companyResponse.data.results || []);
    } catch (err: any) {
      console.error("Failed to fetch companies:", err);
      toast.error("Failed to load companies. Please try again.", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
    } finally {
      setCompanyLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | { name?: string; value: unknown }
    >
  ) => {
    const { name, value } = e.target;
    if (name) {
      setBookingData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleContactChange = (
    event: React.SyntheticEvent,
    value: IContactFormData | null
  ) => {
    setBookingData((prev) => ({
      ...prev,
      contact_id: Number(value?.id),
      first_name: value?.first_name || "",
      surname: value?.last_name || "",
      email: value?.email || "",
      phone: value?.phone || "",
    }));
  };

  const handleCompanyChange = (
    event: React.SyntheticEvent,
    value: IContactFormData | null
  ) => {
    setBookingData((prev) => ({
      ...prev,
      company_name: value?.name || "",
      company_id: value?.id || "",
      branch_name: "",
      branch_rep: "",
      representative_email: "",
      representative_phone: "",
    }));
  };

  const handleBranchChange = (
    event: React.SyntheticEvent,
    value: IBranch | null
  ) => {
    setBookingData((prev) => ({
      ...prev,
      branch_name: value?.name || "",
      branch_rep: value?.branch_rep || "",
      representative_email: value?.email || "",
      representative_phone: value?.phone_number || "",
    }));
  };

  const handleOpenAddBranchDialog = () => {
    setNewBranch({
      company_id: bookingData.company_id || "",
      name: "",
      branch_rep: "",
      email: "",
      phone_number: "",
    });
    setOpenAddBranchDialog(true);
    setErrorMessage("");
  };

  const handleCloseAddBranchDialog = () => {
    setOpenAddBranchDialog(false);
    setNewBranch({
      company_id: "",
      name: "",
      branch_rep: "",
      email: "",
      phone_number: "",
    });
    setErrorMessage("");
  };

  //Add Branch to a company
  const handleAddBranch = async () => {
    setLoading(true);
    try {
      const response = await apiService.post<IBranch>(
        "/api/companies/company-branch/",
        {
          company_id: newBranch.company_id,
          name: newBranch.name,
          branch_rep: newBranch.branch_rep,
          email: newBranch.email,
          phone: newBranch.phone_number,
        }
      );
      setBranchOptions((prev) => [...prev, response.data]);
      setBookingData((prev) => ({
        ...prev,
        branch_name: response.data.name,
        branch_rep: response.data.branch_rep,
        representative_email: response.data.email,
        representative_phone: response.data.phone_number,
      }));
      toast.success("Branch added successfully", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
      handleCloseAddBranchDialog();
    } catch (err: any) {
      console.error("Failed to add branch:", err);
      const errorMessage =
        err.response?.data?.detail || "Failed to add branch. Please try again.";
      setErrorMessage(errorMessage);
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
    } finally {
      setLoading(false);
    }
  };

  const clientFields =
    (AddBookingFormFields as BookingFieldsConfig)[serviceType]?.clientFields ||
    [];

  const handleOpenAddContactDialog = () => {
    setOpenAddContactDialog(true);
    setNewContactData({
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
    });
  };
  const handleCloseAddContactDialog = () => {
    setOpenAddContactDialog(false);
  };

  const handleOpenAddCompanyDialog = () => {
    setOpenAddCompanyDialog(true);
    setNewCompanyData({
      name: "",
      email: "",
      phone: "",
    });
  };
  const handleCloseAddCompanyDialog = () => {
    setOpenAddCompanyDialog(false);
  };

  const handleNewContactChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    setNewContactData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePhoneInputChange = (value: string, info: MuiTelInputInfo) => {
    setNewContactData((prev) => ({
      ...prev,
      phone: value,
    }));
  };

  const handlePhoneBranchInputChange = (
    value: string,
    info: MuiTelInputInfo
  ) => {
    setNewBranch((prev) => ({
      ...prev,
      phone_number: value,
    }));
  };
  const handleNewCompanyChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    setNewCompanyData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
  //Save a new contact
  const handleSaveNewContact = async () => {
    setSavingNewContact(true);
    try {
      const res = await apiService.post<IContactFormData>(
        "/api/contacts/contact/",
        {
          ...newContactData,
          contact_type: "individual",
        }
      );
      const savedContact = res.data;

      toast.success("Contact added successfully!", {
        position: "top-right",
        autoClose: 3000,
        theme: "light",
      });

      setContactOptions((prevOptions) => [...prevOptions, savedContact]);

      setBookingData((prevBookingData) => ({
        ...prevBookingData,
        contact_id: Number(savedContact.id),
        first_name: savedContact.first_name || "",
        surname: savedContact.last_name || "",
        email: savedContact.email || "",
        phone: savedContact.phone || "",
      }));

      handleCloseAddContactDialog();
    } catch (error: any) {
      console.error("Failed to save new contact:", error);
      let errorMessage = "Failed to add contact. Please try again.";
      if (error.response?.data) {
        errorMessage =
          Object.values(error.response.data).flat().join(" ") || errorMessage;
      }
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
    } finally {
      setSavingNewContact(false);
    }
  };
  //save new company
  const handleSaveNewCompany = async () => {
    setSavingNewCompany(true);
    try {
      const res = await apiService.post<IContactFormData>(
        "/api/contacts/contact/",
        newCompanyData
      );
      const savedCompany = res.data;

      toast.success("Company added successfully!", {
        position: "top-right",
        autoClose: 3000,
        theme: "light",
      });

      setCompanyOptions((prevOptions) => [...prevOptions, savedCompany]);

      setBookingData((prevBookingData) => ({
        ...prevBookingData,
        company_contact_id: savedCompany.id,
        company_name: savedCompany.name || "",
        email: savedCompany.email || "",
        phone: savedCompany.phone || "",
      }));

      handleCloseAddCompanyDialog();
    } catch (error: any) {
      console.error("Failed to save new company:", error);
      let errorMessage = "Failed to add company. Please try again.";
      if (error.response?.data) {
        errorMessage =
          Object.values(error.response.data).flat().join(" ") || errorMessage;
      }
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
    } finally {
      setSavingNewCompany(false);
    }
  };

  const handlePhoneCompanyInputChange = (
    value: string,
    info: MuiTelInputInfo
  ) => {
    setNewCompanyData((prev) => ({
      ...prev,
      phone: value,
    }));
  };

  const allContactOptions = useMemo(() => {
    const options = [...contactOptions];

    if (currentContact && !options.find((c) => c.id === currentContact.id)) {
      options.unshift(currentContact);
    }

    return options;
  }, [contactOptions, currentContact]);

  const selectedContact: IContactFormData | null =
    allContactOptions.find((c) => c.id === bookingData.contact_id) ||
    (bookingData.contact_id
      ? {
          id: bookingData.contact_id,
          first_name: bookingData.first_name ?? "",
          last_name: bookingData.surname ?? "",
          email: bookingData.email ?? "",
          phone: bookingData.phone ?? "",
        }
      : null);

  return (
    <Box p={2}>
      <Grid container spacing={2}>
        {clientFields.map((field) => (
          <Grid size={{ xs: 12, md: 4 }} key={field.name}>
            {(serviceType === "FUNERAL_HOME" || serviceType === "EVENT") &&
            (field.name === "first_name" ||
              field.name === "company_name" ||
              field.name === "branch_name") ? (
              <>
                {field.name === "company_name" && (
                  <div>
                    <Autocomplete
                      fullWidth
                      options={companyOptions}
                      getOptionLabel={(option: IContactFormData) =>
                        option.name || ""
                      }
                      value={
                        companyOptions.find(
                          (c) => c.id === bookingData.company_id
                        ) || null
                      }
                      onChange={(event, newValue: IContactFormData | null) => {
                        setBookingData((prev) => ({
                          ...prev,
                          company_id: newValue ? newValue.id : null,
                          company_name: newValue ? newValue.name : "",
                          email: newValue ? newValue.email || "" : "",
                          phone: newValue ? newValue.phone || "" : "",
                        }));
                      }}
                      onInputChange={(event, newInputValue) => {
                        setCompanySearchTerm(newInputValue);
                      }}
                      renderInput={(params) => (
                        <CustomTextField
                          {...params}
                          label="Company Contact"
                          variant="outlined"
                          helperText="Type at least 3 characters to search companies"
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {companyLoading && (
                                  <CircularProgress
                                    size={20}
                                    sx={{
                                      color:
                                        reduxTheme.primary_colors?.[400] ||
                                        "#614438",
                                    }}
                                  />
                                )}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px",
                              "& fieldset": {
                                borderColor:
                                  reduxTheme.primary_colors?.[300] || "#ddd",
                              },
                              "&:hover fieldset": {
                                borderColor:
                                  reduxTheme.neutral_colors?.[400] || "#bbb",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor:
                                  reduxTheme.primary_colors?.[400] || "#614438",
                              },
                            },
                            "& .MuiInputLabel-root": {
                              color: reduxTheme.primary_colors?.[300] || "#666",
                            },
                            "& .MuiInputBase-input": {
                              color: reduxTheme.neutral_colors?.[100] || "#333",
                            },
                          }}
                        />
                      )}
                    />
                    <Button
                      startIcon={<ArrowRightCircle />}
                      onClick={() => {
                        navigate("/contacts/add-company-contact");
                      }}
                      sx={{
                        mt: 1,

                        color: reduxTheme.primary_colors?.[400] || "#614438",

                        "&:hover": {
                          backgroundColor:
                            reduxTheme.neutral_colors?.[800] || "#f9f9f9",
                        },
                      }}
                    >
                      Add Company Contact
                    </Button>
                  </div>
                )}
                {field.name === "branch_name" && (
                  <div>
                    <Autocomplete
                      fullWidth
                      options={branchOptions}
                      getOptionLabel={(option: IBranch) => option.name || ""}
                      value={
                        branchOptions.find(
                          (b) => b.name === bookingData.branch_name
                        ) || null
                      }
                      onChange={handleBranchChange}
                      disabled={!bookingData.company_id}
                      renderInput={(params) => (
                        <CustomTextField
                          {...params}
                          label="Branch"
                          variant="outlined"
                          InputProps={{
                            ...params.InputProps,

                            endAdornment: (
                              <>
                                {branchLoading && (
                                  <CircularProgress
                                    size={20}
                                    sx={{
                                      color:
                                        reduxTheme.primary_colors?.[400] ||
                                        "#614438",
                                    }}
                                  />
                                )}

                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />

                    <Button
                      startIcon={<AddCircleOutlineOutlined />}
                      onClick={handleOpenAddBranchDialog}
                      disabled={!bookingData.company_id}
                      sx={{
                        mt: 1,

                        color: reduxTheme.primary_colors?.[400] || "#614438",

                        "&:hover": {
                          backgroundColor:
                            reduxTheme.neutral_colors?.[800] || "#f9f9f9",
                        },
                      }}
                    >
                      Add Branch
                    </Button>
                  </div>
                )}
              </>
            ) : field.name === "first_name" ? (
              <div>
                <Autocomplete
                  fullWidth
                  options={allContactOptions}
                  getOptionLabel={(option: IContactFormData) =>
                    `${option.first_name || ""} ${
                      option.last_name || ""
                    }`.trim()
                  }
                  // value={
                  //   contactOptions.find(
                  //     (c) => c.id === bookingData.contact_id
                  //   ) || null
                  // }
                  value={selectedContact}
                  onChange={(event, newValue: IContactFormData | null) => {
                    handleContactChange(event, newValue);
                    setBookingData((prevBookingData) => ({
                      ...prevBookingData,
                      contact_id: newValue ? newValue.id : null,
                      first_name: newValue ? newValue.first_name : "",
                      surname: newValue ? newValue.last_name : "",
                      email: newValue ? newValue.email || "" : "",
                      phone: newValue ? newValue.phone || "" : "",
                    }));
                  }}
                  onInputChange={(event, newInputValue) => {
                    setContactSearchTerm(newInputValue);
                  }}
                  loading={contactLoading}
                  renderInput={(params) => (
                    <CustomTextField
                      {...params}
                      label="Client Name"
                      variant="outlined"
                      helperText="Type at least 3 characters to search contacts"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {contactLoading && (
                              <CircularProgress
                                size={20}
                                sx={{
                                  color:
                                    reduxTheme.primary_colors?.[400] ||
                                    "#614438",
                                }}
                              />
                            )}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "10px",
                          "& fieldset": {
                            borderColor:
                              reduxTheme.primary_colors?.[300] || "#ddd",
                          },
                          "&:hover fieldset": {
                            borderColor:
                              reduxTheme.neutral_colors?.[400] || "#bbb",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor:
                              reduxTheme.primary_colors?.[800] || "#614438",
                          },
                        },
                        "& .MuiInputLabel-root": {
                          color: reduxTheme.neutral_colors?.[400] || "#666",
                        },
                        "& .MuiInputBase-input": {
                          color: reduxTheme.neutral_colors?.[100] || "#333",
                        },
                      }}
                    />
                  )}
                />

                <Button
                  startIcon={<AddCircleOutlineOutlined />}
                  onClick={handleOpenAddContactDialog}
                  sx={{
                    mt: 1,

                    color: reduxTheme.primary_colors?.[400] || "#614438",

                    "&:hover": {
                      backgroundColor:
                        reduxTheme.neutral_colors?.[800] || "#f9f9f9",
                    },
                  }}
                >
                  Add Contact
                </Button>
              </div>
            ) : (
              <CustomTextField
                fullWidth
                select={field.type === "select"}
                type={field.type === "date" ? "date" : "text"}
                label={field.label}
                name={field.name}
                disabled={field.disabled}
                value={bookingData[field.name] || ""}
                onChange={handleChange}
                variant="outlined"
                InputLabelProps={
                  field.type === "date" ? { shrink: true } : undefined
                }
              >
                {field.type === "select" && (
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                )}
                {field.type === "select" &&
                  field.options?.map((option, idx) => {
                    const optionValue =
                      typeof option === "object"
                        ? (option as { value: string }).value
                        : option;
                    const optionLabel =
                      typeof option === "object"
                        ? (option as { label: string }).label
                        : option;

                    return (
                      <MenuItem
                        key={`${field.name}-${optionValue}-${idx}`}
                        value={optionValue}
                      >
                        {optionLabel}
                      </MenuItem>
                    );
                  })}
              </CustomTextField>
            )}
          </Grid>
        ))}
      </Grid>

      <Dialog
        open={openAddBranchDialog}
        onClose={handleCloseAddBranchDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            backgroundColor: reduxTheme.neutral_colors?.[900] || "#fff",
            color: reduxTheme.neutral_colors?.[100] || "#333",
          }}
        >
          Add New Branch
        </DialogTitle>
        <DialogContent
          sx={{
            backgroundColor: reduxTheme.neutral_colors?.[900] || "#fff",
          }}
        >
          {errorMessage && (
            <Typography color="error" mb={2}>
              {errorMessage}
            </Typography>
          )}
          <Autocomplete
            fullWidth
            options={companyOptions}
            getOptionLabel={(option: IContactFormData) => option.name || ""}
            value={
              companyOptions.find((c) => c.id === newBranch.company_id) || null
            }
            onChange={(event, value: IContactFormData | null) =>
              setNewBranch((prev) => ({
                ...prev,
                company_id: value?.id || "",
              }))
            }
            renderInput={(params) => (
              <CustomTextField
                {...params}
                label="Company"
                variant="outlined"
                margin="normal"
                required
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: reduxTheme.primary_colors?.[300] || "#ddd",
                    },
                    "&:hover fieldset": {
                      borderColor: reduxTheme.neutral_colors?.[400] || "#bbb",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor:
                        reduxTheme.primary_colors?.[400] || "#614438",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: reduxTheme.primary_colors?.[300] || "#666",
                  },
                  "& .MuiInputBase-input": {
                    color: reduxTheme.neutral_colors?.[100] || "#333",
                  },
                }}
              />
            )}
          />
          <CustomTextField
            fullWidth
            label="Branch Name"
            name="name"
            value={newBranch.name}
            onChange={(e) =>
              setNewBranch((prev) => ({ ...prev, name: e.target.value }))
            }
            variant="outlined"
            margin="normal"
            required
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: reduxTheme.primary_colors?.[300] || "#ddd",
                },
                "&:hover fieldset": {
                  borderColor: reduxTheme.neutral_colors?.[400] || "#bbb",
                },
                "&.Mui-focused fieldset": {
                  borderColor: reduxTheme.primary_colors?.[400] || "#614438",
                },
              },
              "& .MuiInputLabel-root": {
                color: reduxTheme.primary_colors?.[300] || "#666",
              },
              "& .MuiInputBase-input": {
                color: reduxTheme.neutral_colors?.[100] || "#333",
              },
            }}
          />
          <CustomTextField
            fullWidth
            label="Branch Representative"
            name="branch_rep"
            value={newBranch.branch_rep}
            onChange={(e) =>
              setNewBranch((prev) => ({ ...prev, branch_rep: e.target.value }))
            }
            variant="outlined"
            margin="normal"
            required
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: reduxTheme.primary_colors?.[300] || "#ddd",
                },
                "&:hover fieldset": {
                  borderColor: reduxTheme.neutral_colors?.[400] || "#bbb",
                },
                "&.Mui-focused fieldset": {
                  borderColor: reduxTheme.primary_colors?.[400] || "#614438",
                },
              },
              "& .MuiInputLabel-root": {
                color: reduxTheme.primary_colors?.[300] || "#666",
              },
              "& .MuiInputBase-input": {
                color: reduxTheme.neutral_colors?.[100] || "#333",
              },
            }}
          />
          <CustomTextField
            fullWidth
            label="Email"
            name="email"
            value={newBranch.email}
            onChange={(e) =>
              setNewBranch((prev) => ({ ...prev, email: e.target.value }))
            }
            variant="outlined"
            margin="normal"
            required
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: reduxTheme.primary_colors?.[300] || "#ddd",
                },
                "&:hover fieldset": {
                  borderColor: reduxTheme.neutral_colors?.[400] || "#bbb",
                },
                "&.Mui-focused fieldset": {
                  borderColor: reduxTheme.primary_colors?.[400] || "#614438",
                },
              },
              "& .MuiInputLabel-root": {
                color: reduxTheme.primary_colors?.[300] || "#666",
              },
              "& .MuiInputBase-input": {
                color: reduxTheme.neutral_colors?.[100] || "#333",
              },
            }}
          />

          <MuiTelInput
            margin="dense"
            name="phone"
            label="Phone Number"
            fullWidth
            variant="outlined"
            value={newBranch.phone_number || ""}
            defaultCountry="ZW"
            onChange={handlePhoneBranchInputChange}
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions
          sx={{
            backgroundColor: reduxTheme.neutral_colors?.[900] || "#fff",
          }}
        >
          <Button
            onClick={handleCloseAddBranchDialog}
            sx={{
              color: reduxTheme.primary_colors?.[100] || "#614438",
              borderColor: reduxTheme.primary_colors?.[100] || "#ddd",
              backgroundColor: reduxTheme.primary_colors?.[800],
              "&:hover": {
                borderColor: reduxTheme.neutral_colors?.[400] || "#bbb",
                backgroundColor: reduxTheme.neutral_colors?.[800] || "#f9f9f9",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddBranch}
            sx={{
              backgroundColor: reduxTheme.primary_colors?.[400] || "#614438",
              color: reduxTheme.primary_colors?.[900] || "#fff",
              "&:hover": {
                backgroundColor: reduxTheme.primary_colors?.[500] || "#503629",
              },
              "&.Mui-disabled": {
                backgroundColor: reduxTheme.neutral_colors?.[400] || "#ddd",
                color: reduxTheme.primary_colors?.[900] || "#666",
              },
            }}
            variant="contained"
            disabled={
              loading ||
              !newBranch.company_id ||
              !newBranch.name ||
              !newBranch.branch_rep ||
              !newBranch.email ||
              !newBranch.phone_number
            }
          >
            {loading ? (
              <CircularProgress
                size={20}
                sx={{ color: reduxTheme.neutral_colors?.[100] || "#fff" }}
              />
            ) : (
              "Add Branch"
            )}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={openAddContactDialog} onClose={handleCloseAddContactDialog}>
        <DialogTitle>Add New Contact</DialogTitle>
        <DialogContent>
          <CustomTextField
            autoFocus
            margin="dense"
            name="first_name"
            label="First Name"
            type="text"
            fullWidth
            variant="outlined"
            value={newContactData.first_name}
            onChange={handleNewContactChange}
            sx={{ mb: 2 }}
          />
          <CustomTextField
            margin="dense"
            name="last_name" // Assuming your backend expects last_name
            label="Surname"
            type="text"
            fullWidth
            variant="outlined"
            value={newContactData.last_name}
            onChange={handleNewContactChange}
            sx={{ mb: 2 }}
          />
          <CustomTextField
            margin="dense"
            name="email"
            label="Email Address"
            type="email"
            fullWidth
            variant="outlined"
            value={newContactData.email}
            onChange={handleNewContactChange}
            sx={{ mb: 2 }}
          />
          <MuiTelInput
            margin="dense"
            name="phone"
            label="Phone Number"
            fullWidth
            variant="outlined"
            value={newContactData.phone || ""}
            defaultCountry="ZW"
            onChange={handlePhoneInputChange}
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddContactDialog} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleSaveNewContact}
            color="primary"
            disabled={savingNewContact}
          >
            {savingNewContact ? <CircularProgress size={24} /> : "Save Contact"}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={openAddCompanyDialog} onClose={handleCloseAddCompanyDialog}>
        <DialogTitle>Add New Company</DialogTitle>
        <DialogContent>
          <CustomTextField
            autoFocus
            margin="dense"
            name="name"
            label="Company Name"
            type="text"
            fullWidth
            variant="outlined"
            value={newCompanyData.name}
            onChange={handleNewCompanyChange}
            sx={{ mb: 2 }}
          />
          <CustomTextField
            margin="dense"
            name="email"
            label="Email Address"
            type="email"
            fullWidth
            variant="outlined"
            value={newCompanyData.email}
            onChange={handleNewCompanyChange}
            sx={{ mb: 2 }}
          />
          <MuiTelInput
            margin="dense"
            name="phone"
            label="Phone Number"
            fullWidth
            variant="outlined"
            value={newCompanyData.phone || ""}
            defaultCountry="ZW"
            onChange={handlePhoneCompanyInputChange}
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddCompanyDialog} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleSaveNewCompany}
            color="primary"
            disabled={savingNewCompany}
          >
            {savingNewCompany ? <CircularProgress size={24} /> : "Save Company"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BookingClientInformation;
